<template>
  <div class="flex min-h-screen items-center justify-center bg-gray-100 p-4 sm:p-6">
    <div class="grid w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl md:grid-cols-2">
      <!-- Branded / product panel -->
      <div class="relative hidden flex-col bg-gray-900 p-10 md:flex">
        <div class="pointer-events-none absolute inset-0 bg-[radial-gradient(600px_300px_at_0%_100%,rgba(253,204,14,0.18),transparent_60%)]"></div>
        <div class="relative flex h-full flex-col">
          <div class="self-start rounded-xl bg-white px-4 py-2.5 shadow-lg">
            <v-img :src="require('../assets/int_logo.png')" width="180" :aspect-ratio="417/112" contain></v-img>
          </div>

          <h1 class="mt-8 text-3xl font-extrabold leading-tight text-white">INT Ticketing System</h1>
          <p class="mt-3 max-w-sm text-sm leading-relaxed text-white/70">
            A complete work-management platform for INT Logistics — plan sprints, resolve IT
            requests, run QA, and keep every team aligned, all in one place.
          </p>

          <div class="mt-7 grid grid-cols-1 gap-4">
            <div class="flex items-start gap-3">
              <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-400/15 text-amber-400"><v-icon size="20">mdi-view-column-outline</v-icon></span>
              <div>
                <p class="text-sm font-semibold text-white">Scrum boards &amp; sprints</p>
                <p class="text-xs text-white/55">Drag-and-drop kanban, WIP limits, burndown &amp; workload.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-400/15 text-amber-400"><v-icon size="20">mdi-lifebuoy</v-icon></span>
              <div>
                <p class="text-sm font-semibold text-white">IT support desk</p>
                <p class="text-xs text-white/55">Track hardware, software, network &amp; access requests.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-400/15 text-amber-400"><v-icon size="20">mdi-test-tube</v-icon></span>
              <div>
                <p class="text-sm font-semibold text-white">QA test cases</p>
                <p class="text-xs text-white/55">Author cases, record executions, log pass/fail results.</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-400/15 text-amber-400"><v-icon size="20">mdi-account-voice</v-icon></span>
              <div>
                <p class="text-sm font-semibold text-white">Daily check-ins &amp; analytics</p>
                <p class="text-xs text-white/55">Async stand-ups, live KPIs, on-time rates &amp; project timelines.</p>
              </div>
            </div>
          </div>

          <div class="mt-auto pt-8 text-xs uppercase tracking-wider text-white/40">INT Logistics &amp; Manpower</div>
        </div>
      </div>

      <!-- Form panel -->
      <div class="flex items-center justify-center p-8 sm:p-10">
        <div class="w-full max-w-sm text-left">
          <!-- Compact brand for mobile (panel is hidden below md) -->
          <div class="mb-6 flex items-center gap-2 md:hidden">
            <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-900"><v-icon color="#FDCC0E" size="20">mdi-ticket-confirmation</v-icon></span>
            <span class="text-lg font-extrabold text-gray-900">INT Ticketing System</span>
          </div>

          <h2 class="text-2xl font-extrabold text-gray-900">Welcome back</h2>
          <p class="mb-7 mt-1 text-sm text-gray-500">Sign in to continue to your dashboard.</p>

          <label class="text-xs font-semibold text-gray-600">Email address</label>
          <v-text-field
            density="comfortable"
            placeholder="you@company.com"
            prepend-inner-icon="mdi-email-outline"
            variant="solo-filled"
            flat
            hide-details
            class="mb-4 mt-1"
            type="email"
            inputmode="email"
            autocapitalize="none"
            autocorrect="off"
            autocomplete="email"
            spellcheck="false"
            v-model="email"
            @keyup.enter="login"
          ></v-text-field>

          <div class="flex items-center justify-between">
            <label class="text-xs font-semibold text-gray-600">Password</label>
            <v-dialog v-model="dialog" max-width="420" persistent>
              <template v-slot:activator="{ props: activatorProps }">
                <button v-bind="activatorProps" type="button" class="text-xs font-semibold text-gray-500 hover:text-gray-800">
                  Forgot password?
                </button>
              </template>
              <v-card prepend-icon="mdi-email" title="Forgot password?" class="pa-2">
                <v-card-text class="text-medium-emphasis">
                  Enter your email and we'll help you reset your password.
                </v-card-text>
                <v-text-field
                  class="mx-4"
                  density="comfortable"
                  placeholder="Enter your Email address"
                  prepend-inner-icon="mdi-email-outline"
                  variant="outlined"
                  type="email"
                  inputmode="email"
                  autocapitalize="none"
                  autocorrect="off"
                  autocomplete="email"
                  spellcheck="false"
                  v-model="forgot_email"
                ></v-text-field>
                <template v-slot:actions>
                  <v-spacer></v-spacer>
                  <v-btn variant="text" @click="dialog = false">Close</v-btn>
                  <v-btn color="primary" variant="flat" @click="dialog = false">Confirm</v-btn>
                </template>
              </v-card>
            </v-dialog>
          </div>

          <v-text-field
            :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
            :type="visible ? 'text' : 'password'"
            density="comfortable"
            placeholder="Enter your password"
            prepend-inner-icon="mdi-lock-outline"
            variant="solo-filled"
            flat
            hide-details
            class="mb-2 mt-1"
            @click:append-inner="visible = !visible"
            v-model="password"
            @keyup.enter="login"
          ></v-text-field>

          <v-checkbox
            v-model="rememberMe"
            label="Remember me"
            color="primary"
            density="compact"
            hide-details
            class="mb-4"
            @change="saveToLocalStorage"
          ></v-checkbox>

          <button
            type="button"
            @click="login"
            class="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-400 px-4 py-3 text-sm font-bold text-gray-900 shadow-sm transition hover:brightness-105 active:scale-[0.98]"
          >
            Log In
            <v-icon size="18">mdi-arrow-right</v-icon>
          </button>

          <p class="mt-6 text-center text-xs text-gray-400">Authorized users only · INT Logistics &amp; Manpower</p>
        </div>
      </div>
    </div>
    <LoadingOverlay :toggle-loader="isloading"></LoadingOverlay>
    <Snackbar :toggle-snackbar="isshow" :response-message="message"></Snackbar>
  </div>
</template>
  <script>
  import utils from "../utils";
  import constant from "@/constant";
  import axios from "axios";
  import Snackbar from "./Snackbar.vue";
  import LoadingOverlay from "./LoadingOverlay.vue";
  export default {
    name: "LoginPage",
    components: {
        Snackbar,
        LoadingOverlay
    },
    data() {
      return {
        dialog: false,
        isloading: false,
        constant: constant,
        visible: false,
        email: "",
        password: "",
        forgot_email: "",
        isshow: false,
        message: "",
        rememberMe: JSON.parse(localStorage.getItem('rememberMe')) || false
      }
    },
  watch: {
    rememberMe() {
      this.saveToLocalStorage();
    },
  },
  created() {
    this.rememberMe = JSON.parse(localStorage.getItem('rememberMe')) || false;
    if (this.rememberMe) {
      this.email = localStorage.getItem('email') || '';
    }
  },
    methods: {
        saveToLocalStorage() {
            localStorage.setItem('rememberMe', JSON.stringify(this.rememberMe));
            if (this.rememberMe) {
                localStorage.setItem('email', this.email);
            } else {
                localStorage.removeItem('email');
            }
        },
        login(){
            this.isloading = true;
            this.isshow = !this.isshow;
            const f = new FormData();
            f.append("email", this.email);
            f.append("password", this.password);
           axios.post(utils._api(this.constant.login),f ,{
                // headers
            }).then((response) => {
                this.message = response.data.message;
                if (response.data.status === 200) {
                    this.isshow = !this.isshow;
                    const userData = response.data.data;
                    const token = response.data.token;
                    localStorage.setItem('user_data', JSON.stringify(userData));
                    localStorage.setItem('token', token);
                    console.log('Successfully Login!', response.data.message);
                    setTimeout(() => {
                    this.$router.push({ name: 'dashboard' });
                    }, 1000);
                } else {
                    setTimeout(() => {
                        this.isshow = !this.isshow;
                        this.isloading = false;
                    }, 1000);
                }
            }).catch((error) => {
                this.isloading = false;
                this.isshow = true;
                this.message = "An error occurred. Please try again.";
                console.error("Login error:", error);
            });
        }
    }
  }
</script>
